import { inject, Injectable } from '@angular/core';
import { INotificationMessage } from '@app/account/notifications-and-alerts/interface';
import { NotificationActionService } from '@app/account/notifications-and-alerts/notification-action.service';
import {
  ChannelNotification,
  NotificationRuleSeverityEnum,
} from '@app/generated/graphql';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  // dependencies
  private _notificationActionService = inject(NotificationActionService);

  // streams
  private _notificationSubject = new Subject<INotificationMessage[]>();

  notifications$ = this._notificationSubject.asObservable();

  /**
   * The default duration of a notification in milliseconds
   */
  readonly DEFAULT_DURATION: number = 3000;

  /**
   * Transforms backend channel notifications into UI notification messages.
   */
  public transformChannelNotificationsToNotificationMessages(
    notifications: ChannelNotification[],
  ): INotificationMessage[] {
    return notifications.map((notification: ChannelNotification) => ({
      id: notification.id,
      metadata: notification.channelMetadata,
      notificationRuleSlug: notification?.notificationRule?.notificationSlug,
      title: notification?.notificationRule?.name,
      description: notification?.notificationRule?.description,
      displayTitle: notification?.notificationRule?.name,
      displayDescription: notification.body,
      severity:
        notification?.notificationRule?.severity ??
        NotificationRuleSeverityEnum.Info,
      life: this.DEFAULT_DURATION,
      actionLabel: this._notificationActionService.getActionLabel(
        notification?.notificationRule?.notificationSlug,
      ),
      channelNotification: notification,
    }));
  }

  showNotifications(notifications: INotificationMessage[]): void {
    if (!notifications?.length) {
      return;
    }

    const normalizedNotifications: INotificationMessage[] = notifications.map(
      (notification: INotificationMessage) => ({
        ...notification,
        life: notification?.life ?? this.DEFAULT_DURATION,
      }),
    );

    this._notificationSubject.next(normalizedNotifications);
  }
}
